import Joi from 'joi';
import _ from 'lodash';

import { ChangeEvent, Component, FormEvent } from 'react';

import Button from '../themed/button';
import CreatableMultiselectDropdown from '../themed/multiselect/creatable';
import DatePickerInput from '../themed/date-picker';
import Input from '../themed/input/input';
import MultiSelect from '../themed/multiselect';
import Select from '../themed/select/select';
import Switch from '../themed/switch';
import TextArea from './textarea';
import TextEditor from '../themed/text-editor';
import UploadWithPreview from '../themed/file-upload/uploadWithPreview';

import { MAX_IMG_SIZE_IN_BYTES, FILE_UPLOAD_ERRORS } from '../../utils/constants';
import { fileUpload } from '../../services/fileUpload';
import { setPhotoNumber } from '../../store/reportDetails';
import { readSrcFromFiles } from '../../utils/lib';

import * as models from '../../utils/models';
import * as utils from '../../utils/utilities';

import config from '../../utils/config';
import toast from '../../services/toastService';


type FormProps = any;

type FormState = {
	data: any;
	errors: any;
	progress: number;
	loading: boolean;
	touched?: boolean;
};

type SchemaOptions = {
	abortEarly: boolean;
	allowUnknown?: boolean;
};

class Form extends Component<FormProps, FormState> {
	schema: any;

	state: FormState = {
		data: {},
		errors: {},
		progress: 0,
		loading: false,
		touched: false,
	};

	doSubmit = (): void => {};

	createNewMultiSelectOption = (value: string) => {
		return { label: value, value: value };
	}

	handleProgressChange = (e: ProgressEvent): void => {
		const progress: number = (e.loaded * 100) / e.total;
		this.setState({ progress });

		const electronApi: any = (window as any).api;
		if (electronApi) electronApi.setProgress(progress / 100);
	};

	validate = (): any | null => {
		const options: SchemaOptions = { abortEarly: true, allowUnknown: true };

		const hasExistingErrors = Object.keys(this.state.errors).length > 0;
		if (hasExistingErrors) {
			const errorValues = Object.values(this.state.errors);
			if (errorValues.includes(FILE_UPLOAD_ERRORS.PHOTO_CAPTION_UPDATE_COMMENT)) {
				return this.state.errors;
			}
		}

		const result = Joi.object(this.schema).validate(this.state.data, options);
		if (!result.error) return null;

		const errors: any = {};
		result.error.details.forEach((e: any) => (errors[e.path[0]] = e.message));
		return errors;
	};

	validateProperty = (
		e: ChangeEvent<HTMLInputElement | HTMLSelectElement>
	): string | null => {
		const input: HTMLInputElement | HTMLSelectElement = e.target;
		const data: object = { [input.name]: input.value };
		const schema: object = { [input.name]: this.schema[input.name] };

		const result: any = Joi.object(schema).validate(data);
		return result.error ? result.error.details[0].message : null;
	};

	handleChange = (e: ChangeEvent<HTMLInputElement | HTMLSelectElement>): void => {
		const input: HTMLInputElement | HTMLSelectElement = e.target;

		const errors: any = { ...this.state.errors };
		const errorMessage: string | null = this.validateProperty(e);

		if (errorMessage) errors[input.name] = errorMessage;
		else delete errors[input.name];

		this.handleDataChange(input.name, input.value);
		this.setState({ errors, touched: true });
	};

	handleDataChange = (name: string, value: any) => {
		const data: any = { ...this.state.data };
		data[name] = value;

		this.setState({ data });
	};

	validateFileUpload = (files: models.PDFImageProps[]) => {
		if (!utils.getFileCaptionsUnique(files)) {
			return FILE_UPLOAD_ERRORS.DUPLICATE_CAPTIONS_COMMENT;
		}

		if (!utils.getMultipleFileCaptionsValid(files)) {
			return FILE_UPLOAD_ERRORS.PHOTO_CAPTION_UPDATE_COMMENT;
		}

		if (!utils.getPrefixNotFoundInMultipleFiles(files)) {
			return FILE_UPLOAD_ERRORS.PHOTO_PREFIX_FOUND;
		}

		return '';
	};

	handleFileUpload = async (files: File[], params: any): Promise<void> => {
		const { types, name, max = 2 } = params;

		const errors: any = { ...this.state.errors };
		const data: any = { ...this.state.data };

		const isValidType: boolean = utils.validateFileType(files, types);
		const isValidLength: boolean = utils.validateFileLength(files, max);
		const isValidSize: boolean = utils.validateFileSize(files, MAX_IMG_SIZE_IN_BYTES);

		if (isValidType && isValidSize && isValidLength) {
			this.setState({ loading: true });

			const canAccommodateAnotherFile = data[name].length > 0 && data[name].length < max;
			if ((!navigator.onLine) && (config.useBase64OfflineImage)) {
				const promises = await readSrcFromFiles(files);
				const newFiles = await Promise.all(promises);

				data[name] = canAccommodateAnotherFile ? [...data[name], ...newFiles] : newFiles;
			} else {
				const newFiles = await fileUpload(files);
				data[name] = canAccommodateAnotherFile ? [...data[name], ...newFiles] : newFiles;
			}

			if (!params.hideCaption && params.enforceImageCaptionUpdate) {
				const errorMessage = this.validateFileUpload(data[name]);
				if (errorMessage) errors[name] = errorMessage;
				else delete errors[name];
			} else {
				delete errors[name];
			}

			this.setState({ loading: false });
		} else {
			if (!isValidType) errors[name] = 'File type is not supported!';
			if (!isValidSize) errors[name] = utils.getFileSizeValidationMessage(MAX_IMG_SIZE_IN_BYTES);
			if (!isValidLength) errors[name] = 'Only ' + max + ' allowed!';
		}

		this.setState({ data, errors });
	};

	handleSubmit = (e: FormEvent<HTMLFormElement>): void => {
		e.preventDefault();

		const errors: any = this.validate();
		if (!errors) return this.doSubmit();

		this.setState({ errors: errors || {} }, this.handleFormErrorNotice);
	};

	handleFormErrorNotice = (): void => {
		const firstErrorField: string = Object.keys(this.state.errors)[0];

		const notice: string = this.state.errors[firstErrorField];
		toast.error(notice);

		const selector: string = `input[name="${firstErrorField}"], input[name="search_name_input"], select[name="${firstErrorField}"], textarea[name="${firstErrorField}"]`;
		const element = document.querySelector(selector);
		if (!element) return;

		element.scrollIntoView({ behavior: 'smooth' });
	};

	renderButton = (label: string): JSX.Element => {
		return <Button label={label} disabled={this.state.loading} type="submit" />;
	};

	renderTextEditor = (params: { name: string; label: string; placeholder: string; }) => {
		const { data, errors } = this.state;
		const { name, label, placeholder } = params;

		return (
			<TextEditor
					name={name}
					label={label}
					error={errors[name]}
					value={data[name]}
					placeholder={placeholder}
					onChange={(text) => this.handleDataChange(name, text)} 
				/>
		);
	}

	renderDatePicker = (params: any) => {
		const { data, errors } = this.state;
		const { name, ...rest } = params;

		const handleDateChange = (date: Date) => {
			this.handleChange({ target: { name, value: date }} as any);
		};

		return (
			<DatePickerInput
				date={data[name]}
				error={errors[name]}
				onDateChange={handleDateChange}
				{...rest}
			/>
		);
	};

	renderInput = (params: any): JSX.Element => {
		const { data, errors } = this.state;
		const { name, label, type, ...rest } = params;

		return (
			<Input
				type={type}
				id={name}
				name={name}
				value={data[name]}
				error={errors[name]}
				label={label}
				onChange={this.handleChange}
				{...rest}
			/>
		);
	};

	renderMultiSelect = (params: any) => {
		const { data, errors } = this.state;

		const handleAction = (items: models.SelectOption[]) => {
			this.handleChange({ target: { name: params.name, value: items }} as any);
		};

		return (
			<MultiSelect
				onSelect={handleAction}
				onRemove={handleAction}
				selectedValues={data[params.name]}
				error={errors[params.name]}
				{...params}
			/>
		);
	};

	renderCreatableMultiselect = (params: any) => {
		const { data, errors } = this.state;

		const handleAction = (items: models.SelectOption[]) => {
			this.handleChange( { target: { name: params.name, value: items }} as any);
		};

		return (
			<CreatableMultiselectDropdown
				onSelect={handleAction}
				selectedValues={data[params.name]}
				error={errors[params.name]}
				{...params}
			/>
		);
	}

	renderTextArea = (params: any): JSX.Element => {
		const { data, errors } = this.state;
		const { name, label, ...rest } = params;

		return (
			<TextArea
				name={name}
				value={data[name]}
				error={errors[name]}
				label={label}
				onChange={this.handleChange}
				{...rest}
			/>
		);
	};

	renderFileInput = (params: any): JSX.Element => {
		const { name } = params;
		const { data, errors } = this.state;

		const handleFileRemove = (file: models.PDFImageProps): void => {
			let newFiles: models.PDFImageProps[] = data[name].filter((image: models.PDFImageProps) => {
				return image.refNo !== file.refNo;
			});

			if (newFiles.length > 1) {
				let orderedFiles = _.orderBy(newFiles, ['refNo'], 'asc');
				let initialRefNo = +orderedFiles[0].refNo;

				let adjustedFiles = newFiles.slice(1).map((file) => ({ ...file, refNo: ++initialRefNo }));
				newFiles = [orderedFiles[0], ...adjustedFiles];
			}

			setPhotoNumber(newFiles.length);

			this.handleChange({ target: { name, value: newFiles }} as any);
		};

		const handleFileEdit = (file: models.PDFImageProps) => {
			const newFiles: models.PDFImageProps[] = data[name].map((image: models.PDFImageProps) => {
				if (image.refNo === file.refNo)
					return {
						refNo: file.refNo,
						src: file.src,
						caption: file.caption,
						format: file.format,
					};

				return image;
			});

			const newData = { ...this.state.data, [name]: newFiles };

			if (params.enforceImageCaptionUpdate) {
				const errors = {...this.state.errors};
				const errorMessage = this.validateFileUpload(newFiles);

				if (errorMessage) errors[name] = errorMessage;
				else delete errors[name];

				this.setState({ data: newData, errors });
			} else {
				this.setState({ data: newData });
			}
		};

		return (
			<UploadWithPreview
				error={errors[name]}
				files={data[name]}
				loading={this.state.loading}
				onDrop={(files: File[]) => this.handleFileUpload(files, params)}
				onFilesChange={(images: models.PDFImageProps[]) => this.handleChange({ target: { name, value: images }} as any)}
				onFileEdit={handleFileEdit}
				onFileRemove={handleFileRemove}
				{...params}
			/>
		);
	};
	
	renderSelect = (params: models.SelectProps): JSX.Element => {
		const { name, label, options, ...rest } = params;
		const { data, errors } = this.state;

		return (
			<Select
				name={name}
				value={data[name]}
				error={errors[name]}
				label={label}
				onChange={this.handleChange}
				options={options}
				{...rest}
			/>
		);
	};

	renderSwitch = (params: any): JSX.Element => {
		const { name, label, ...otherProps } = params;
		const { data } = this.state;

		return (
			<Switch
				{...otherProps}
				checked={data[name]}
				onChange={() => this.handleDataChange(name, !data[name])}
				label={label}
			/>
		);
	};
}

export default Form;

